import { useEffect, useRef, useState } from 'react';
import HeroSlide from './HeroSlide';
import HeroPagerDots from './HeroPagerDots';
import { translateContentText } from '../../locales/content';

const AUTO_PLAY_MS = 5000;
const SWIPE_THRESHOLD = 50; // px

/**
 * HeroCarousel — wrapper над HeroSlide с auto-play + swipe.
 *
 * Figma-spec §3: dots max 6, handoff §3 допускает до 6 баннеров.
 * Swipe работает через pointer-events (touch + mouse drag).
 * Edge case (acceptance §2.4): banners=[] → null (не крэшим).
 *
 * Props:
 *   banners     — HeroBanner[]
 *   onSlideTap? — (banner) => void; по tap на слайд
 */
export default function HeroCarousel({ banners, onSlideTap }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const dragStartX = useRef(null);
  const timerRef = useRef(null);

  const count = banners?.length || 0;

  useEffect(() => {
    if (count <= 1) return undefined;
    timerRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % count);
    }, AUTO_PLAY_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [count]);

  if (count === 0) return null;

  const current = banners[activeIdx] || banners[0];

  const resetTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      if (count > 1) {
        timerRef.current = setInterval(() => {
          setActiveIdx((i) => (i + 1) % count);
        }, AUTO_PLAY_MS);
      }
    }
  };

  const onPointerDown = (e) => {
    dragStartX.current = e.clientX ?? e.touches?.[0]?.clientX ?? null;
  };

  const onPointerUp = (e) => {
    if (dragStartX.current == null) return;
    const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? null;
    if (endX == null) {
      dragStartX.current = null;
      return;
    }
    const dx = endX - dragStartX.current;
    dragStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0) {
      // swipe left → next
      setActiveIdx((i) => (i + 1) % count);
    } else {
      // swipe right → prev
      setActiveIdx((i) => (i - 1 + count) % count);
    }
    resetTimer();
  };

  return (
    <div
      data-testid="hero-carousel"
      className="relative w-full"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      <HeroSlide
        key={`${current.id}:${current.imageUrl || ''}:${current.videoUrl || ''}:${current.titleImageUrl || ''}`}
        imageUrl={current.imageUrl}
        videoUrl={current.videoUrl}
        titleImageUrl={current.titleImageUrl}
        titleImageWidth={current.titleImageWidth}
        titleImageHeight={current.titleImageHeight}
        ctaText={translateContentText(current.ctaText)}
        onCtaClick={() => onSlideTap && onSlideTap(current)}
      />

      {count > 1 && (
        <div className="absolute left-6 bottom-6">
          <HeroPagerDots count={count} active={activeIdx} />
        </div>
      )}
    </div>
  );
}
